/**
 * InboxPage (`/dashboard/posteingang`) — the free inbound archive (WL-A6).
 *
 * Lists `inbound_documents` PAGINATED (limit 25 + startAfter cursor — read-storm
 * safe, CFO), with §10 status badges, a detail view (extracted fields + artifact
 * downloads via `getInboundDocumentFile`), the receive address + supplier setup
 * guide, and a fair-use meter. Free users get the FULL page (no premium lock —
 * the deliberate contrast to the legacy received-invoices page). "Als Ausgabe
 * buchen" reuses the M4 `ExpenseBookingModal` (Premium only). NO delete button.
 *
 * Route registration (App.tsx) is gated on `INBOUND_ROUTE_ENABLED` (flag OR
 * Beta-Tenant-Allowlist — reachable for more than the per-tenant decision), the
 * menu entry (MainLayout) and this page's own content are gated on
 * `canUseInbound(effectiveScopeUid)` (config/inboundAccess.ts). The page-level
 * guard below is the direct-URL fail-closed: without it, a non-beta tenant
 * navigating straight to `/dashboard/posteingang` (route registered because
 * the beta allowlist is non-empty) would see an empty-but-rendered page instead
 * of nothing (same `return null` convention as `DatevExportPage`/`ReceiptScanPage`).
 *
 * @module InboxPage
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconInbox, IconRefresh } from '../icons';
import {
  collection,
  doc as fsDoc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { useIsReadOnly } from '../hooks/useIsReadOnly';
import type { InboundDocument } from '../types/inboundTypes';
import { inboundToParsedZugferd } from '../utils/inboundAdapters';
import InboundDocumentCard from '../components/inbound/InboundDocumentCard';
import InboundDocumentDetail from '../components/inbound/InboundDocumentDetail';
import InboundSetupGuide from '../components/inbound/InboundSetupGuide';
import InboundExportSection from '../components/inbound/InboundExportSection';
import InboundDatevExportSection from '../components/inbound/InboundDatevExportSection';
import FairUseMeter from '../components/inbound/FairUseMeter';
import ExpenseBookingModal from '../components/expenses/ExpenseBookingModal';
import { canUseInbound, canUseInboundTaxDecision } from '../config/inboundAccess';
import { canUseInboundDatev } from '../config/inboundDatevAccess';
import { resolveTier } from '../config/entitlements';
import { isLoopBBeta } from '../config/loopBBetaAccess';
import { canUseDocChecks } from '../config/kanzleiAccess';
import type { SkrKontenrahmen } from '../utils/datevMapping';

// ── Feature flags (fail-closed: default false unless explicitly 'true') ───────
const EXPENSES_ENABLED = process.env.REACT_APP_EXPENSES_ENABLED === 'true';

/** Page size for the paginated inbox query (§5.9 composite index). */
const PAGE_SIZE = 25;

const InboxPage: React.FC = () => {
  const { t } = useTranslation('posteingang');
  const { currentUser, isPremium, effectiveScopeUid, sellerData } = useAuth();
  const isReadOnly = useIsReadOnly();

  // IDV-6: Seller-DATEV-Stammdaten (Muster `DatevExportPage`) — Default-Header-
  // Werte für den Eingangsbelege-Belegtransfer. `sellerData` ist die des
  // aktuell eingeloggten Users (AuthContext-Verhalten, nicht IDV-6-spezifisch).
  const sellerDatevConfig = useMemo(
    () => ({
      skr: (sellerData?.datevSkr as SkrKontenrahmen | undefined) ?? 'SKR03',
      beraterNr: sellerData?.datevBeraterNr ?? null,
      mandantenNr: sellerData?.datevMandantenNr ?? null,
    }),
    [sellerData?.datevSkr, sellerData?.datevBeraterNr, sellerData?.datevMandantenNr],
  );

  const [docs, setDocs] = useState<InboundDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [selected, setSelected] = useState<InboundDocument | null>(null);

  const [mailboxAddress, setMailboxAddress] = useState<string | null>(null);
  const [monthCount, setMonthCount] = useState<number>(0);
  const [bookingDoc, setBookingDoc] = useState<InboundDocument | null>(null);
  const [expensedIds, setExpensedIds] = useState<Set<string>>(new Set());

  // Generation counter for the first-page load — replaces the `cancelled`
  // closure-flag from the original mount-effect (Reviewer-Fix F1, ui-polish).
  // Bumped at the start of every `refresh()` call (mount, scope switch via the
  // `effectiveScopeUid`-dependent useCallback identity change, and manual
  // refresh-button clicks/StrictMode double-invoke); a resolving `getDocs`
  // whose generation no longer matches the ref is a stale response and MUST
  // NOT write `docs`/`cursor`/`hasMore`/`loading`. `handleLoadMore` reads
  // (without bumping) the generation captured at its own start so that a
  // scope switch mid-pagination — which triggers a fresh `refresh()` and
  // therefore a new generation — also discards a late-resolving "load more".
  const loadGenerationRef = useRef(0);

  /**
   * Maps a Firestore snapshot to a typed inbound document. W2 (WL-K11 §4.8 v3.1):
   * `verified.sourceInvoiceRef` is a cross-tenant identifier (opaque foreign-tenant
   * doc-id) and is STRIPPED here — the client keeps only `verifiedAgainstOriginal`
   * + `verificationSource`. (Server-only enforcement is Firestore rules; this strip
   * additionally keeps the ref out of the FE state/render surface.)
   */
  const mapSnap = useCallback(
    (snap: QueryDocumentSnapshot<DocumentData>): InboundDocument => {
      const raw = snap.data() as Omit<InboundDocument, 'id'> & {
        verified?: (InboundDocument['verified'] & { sourceInvoiceRef?: unknown }) | null;
      };
      const { verified, ...rest } = raw;
      const safeVerified =
        verified && typeof verified === 'object'
          ? {
              verifiedAgainstOriginal: verified.verifiedAgainstOriginal,
              verificationSource: verified.verificationSource,
              matchedAt: verified.matchedAt ?? null,
            }
          : verified ?? null;
      return { id: snap.id, ...rest, verified: safeVerified };
    },
    [],
  );

  // ── Load / refresh first page ────────────────────────────────────────────
  // Extracted from the mount-effect below so the same first-page load can be
  // re-triggered by the manual refresh button (no realtime listener on
  // `inbound_documents` — newly received invoices otherwise only appear
  // after a full page reload).
  const refresh = useCallback(async () => {
    const myGen = ++loadGenerationRef.current;
    if (!currentUser) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const q = query(
        collection(db, 'inbound_documents'),
        where('userId', '==', effectiveScopeUid),
        orderBy('receivedAt', 'desc'),
        limit(PAGE_SIZE),
      );
      const snap = await getDocs(q);
      if (myGen !== loadGenerationRef.current) return;
      setDocs(snap.docs.map(mapSnap));
      setCursor(snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null);
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (err) {
      if (myGen !== loadGenerationRef.current) return;
      console.error('InboxPage: failed to load inbound documents', err);
    } finally {
      if (myGen === loadGenerationRef.current) setLoading(false);
    }
  }, [currentUser, effectiveScopeUid, mapSnap]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  // ── Load own mailbox (address + fair-use) — owner context only ────────────
  useEffect(() => {
    if (!currentUser || isReadOnly) return;
    let cancelled = false;
    const load = async () => {
      try {
        const snap = await getDoc(fsDoc(db, 'inbound_mailboxes', currentUser.uid));
        if (cancelled || !snap.exists()) return;
        const data = snap.data() as {
          address?: string;
          fairUse?: { monthCount?: number };
        };
        if (typeof data.address === 'string') setMailboxAddress(data.address);
        if (typeof data.fairUse?.monthCount === 'number') {
          setMonthCount(data.fairUse.monthCount);
        }
      } catch (err) {
        // Non-critical: the list still works without the setup guide/meter.
        console.error('InboxPage: failed to load mailbox', err);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [currentUser, isReadOnly]);

  // ── Load more (pagination cursor) ─────────────────────────────────────────
  const handleLoadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;
    const myGen = loadGenerationRef.current;
    setLoadingMore(true);
    try {
      const q = query(
        collection(db, 'inbound_documents'),
        where('userId', '==', effectiveScopeUid),
        orderBy('receivedAt', 'desc'),
        startAfter(cursor),
        limit(PAGE_SIZE),
      );
      const snap = await getDocs(q);
      if (myGen !== loadGenerationRef.current) return;
      setDocs((prev) => [...prev, ...snap.docs.map(mapSnap)]);
      setCursor(snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null);
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (err) {
      if (myGen !== loadGenerationRef.current) return;
      console.error('InboxPage: failed to load more', err);
    } finally {
      if (myGen === loadGenerationRef.current) setLoadingMore(false);
    }
  }, [cursor, loadingMore, effectiveScopeUid, mapSnap]);

  // ── Expense booking (Premium + flag + owner) ──────────────────────────────
  const canBookExpense = EXPENSES_ENABLED && isPremium && !isReadOnly;

  // ── Manuelle Steuerkontierung (owner-only v1, §5.5): gated auf den
  //    effektiven Tenant-Scope UND currentUser==Scope (Owner). Advisor-Scope-
  //    Ansicht bleibt read-only (kein Schreibpfad, J-3). ────────────────────────
  const canDecideTax =
    canUseInboundTaxDecision(effectiveScopeUid) &&
    !!currentUser?.uid &&
    currentUser.uid === effectiveScopeUid;

  const handleBookExpense = useCallback((doc: InboundDocument) => {
    setBookingDoc(doc);
  }, []);

  // Nach einer Kontierungs-Entscheidung: neuen Summary-Stand in Liste + Detail
  // mergen (kein Full-Reload). `null` = aufgehoben.
  const handleTaxDecided = useCallback(
    (docId: string, taxDecision: InboundDocument['taxDecision']) => {
      setDocs((prev) =>
        prev.map((d) => (d.id === docId ? { ...d, taxDecision } : d)),
      );
      setSelected((prev) =>
        prev && prev.id === docId ? { ...prev, taxDecision } : prev,
      );
    },
    [],
  );

  const handleExpenseBooked = useCallback(() => {
    if (bookingDoc) {
      setExpensedIds((prev) => new Set([...Array.from(prev), bookingDoc.id]));
    }
    setBookingDoc(null);
  }, [bookingDoc]);

  // Fail-closed direct-URL guard (Reviewer-Fix F-2, loop-a-inbound-beta): the
  // route is registered as soon as ANYONE could have access (flag OR non-empty
  // Beta-Allowlist, see App.tsx `INBOUND_ROUTE_ENABLED`) — without this guard a
  // non-beta tenant navigating straight to `/dashboard/posteingang` would see
  // an empty-but-rendered page. All hooks above are called unconditionally
  // first (Rules-of-Hooks), same placement convention as
  // `DatevExportPage`/`ReceiptScanPage` (`if (!canUseX(...)) return null;`
  // right before the render branches).
  if (!canUseInbound(effectiveScopeUid)) return null;

  // ── Detail view ───────────────────────────────────────────────────────────
  if (selected) {
    return (
      <div className="min-h-screen bg-surface p-4 md:p-6">
        <div className="max-w-4xl mx-auto">
          <InboundDocumentDetail
            doc={selected}
            onBack={() => setSelected(null)}
            canBookExpense={canBookExpense && !expensedIds.has(selected.id)}
            onBookExpense={handleBookExpense}
            // WL-K11 / M-GA-4: the Verified seal is Cluster D (docChecks) — gated
            // on the viewer's OWN identity + sellerData via canUseDocChecks, never
            // on effectiveScopeUid.
            showVerifiedSeal={canUseDocChecks(currentUser?.uid, sellerData)}
            // WL-K12: the "USt-IdNr. prüfen"-Aktion is Cluster H (BZSt/VIES) —
            // deliberately NOT decoupled, stays on isLoopBBeta (own decision).
            showVatCheck={isLoopBBeta(currentUser?.uid, sellerData)}
            // Manuelle Steuerkontierung (§5.4/§6): owner-only v1, gated.
            canDecideTax={canDecideTax}
            onTaxDecided={handleTaxDecided}
          />
        </div>
        {bookingDoc && (
          <ExpenseBookingModal
            open={true}
            invoice={inboundToParsedZugferd(bookingDoc)}
            onClose={() => setBookingDoc(null)}
            onBooked={handleExpenseBooked}
          />
        )}
      </div>
    );
  }

  // ── List view ─────────────────────────────────────────────────────────────
  return (
    <>
      <div className="min-h-screen bg-surface p-4 md:p-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-primary flex items-center gap-3">
                <IconInbox className="text-accent-ink" size={24} />
                {t('title', 'Posteingang')}
              </h1>
              <p className="text-muted text-sm mt-1">
                {t('subtitle', 'Empfangene Rechnungen — sicher archiviert und auf Formatfehler geprüft.')}
              </p>
            </div>
            {/* Manuelles Neuladen: kein Realtime-Listener auf `inbound_documents`,
                neu eingegangene Belege erscheinen sonst erst nach vollem Reload. */}
            <button
              type="button"
              onClick={refresh}
              disabled={loading}
              title={t('refresh', 'Aktualisieren')}
              aria-label={t('refresh', 'Aktualisieren')}
              data-testid="inbound-refresh"
              className="shrink-0 flex items-center gap-2 text-sm bg-surface-raised hover:bg-surface-inset border border-hairline disabled:opacity-60 text-secondary px-4 py-2 rounded-xl font-medium transition-colors"
            >
              <IconRefresh className={loading ? 'animate-spin' : ''} size={14} />
              {t('refresh', 'Aktualisieren')}
            </button>
          </div>

          {/* Owner-only: setup guide (full width) + fair-use meter as a slim
              full-width strip underneath — NOT a near-empty side card (Welle 1,
              polish: the old lg:col-span-2 + 1/3-card layout left a mostly
              blank right-hand card for non-premium users with a low count and
              an oddly tall empty card for premium users). */}
          {!isReadOnly && mailboxAddress && (
            <div className="mb-6 space-y-3">
              <InboundSetupGuide address={mailboxAddress} />
              <div className="bg-[rgb(var(--surface-raised-rgb)/0.6)] border border-hairline rounded-xl px-4 py-3">
                <FairUseMeter monthCount={monthCount} isPremium={isPremium} />
              </div>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex justify-center py-16 text-muted text-sm">
              {t('loading', 'Wird geladen …')}
            </div>
          )}

          {/* Empty */}
          {!loading && docs.length === 0 && (
            <div className="text-center py-16">
              <IconInbox className="text-gray-600 mx-auto mb-4" size={48} />
              <p className="text-muted font-medium">
                {t('empty.title', 'Noch keine Belege im Posteingang')}
              </p>
              <p className="text-faint text-sm mt-1">
                {t('empty.body', 'Leiten Sie Rechnungen an Ihre Empfangsadresse weiter oder laden Sie sie hoch.')}
              </p>
            </div>
          )}

          {/* List — full-width, single column (Welle 2, polish): a narrow
              multi-column grid made a single received invoice look "lost"
              on the left; a full-width list row matches the width of the
              export box below and lets the amount/sender act as a hero. */}
          {docs.length > 0 && (
            <>
              <h2 className="text-sm font-semibold text-secondary mb-3">
                {t('list.title', 'Empfangene Rechnungen ({{count}})', { count: docs.length })}
              </h2>
              <div className="flex flex-col gap-3">
                {docs.map((d) => (
                  <InboundDocumentCard key={d.id} doc={d} onClick={setSelected} />
                ))}
              </div>
            </>
          )}

          {/* Load more */}
          {!loading && hasMore && (
            <div className="flex justify-center mt-6">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="text-sm bg-surface-raised hover:bg-surface-inset border border-hairline disabled:opacity-60 text-secondary px-5 py-2.5 rounded-xl font-medium transition-colors"
              >
                {loadingMore ? t('loading', 'Wird geladen …') : t('loadMore', 'Mehr laden')}
              </button>
            </div>
          )}

          {/* Owner-only: portability export (WL-A8, DSGVO Art. 20 / I-7).
              Gated auf canUseInbound(effectiveScopeUid) (Beta-Allowlist ODER
              Flag) — NICHT auf das reine Env-Flag: der Server-Callable
              `requestPortabilityExport` (functions/src/inbound/
              portabilityExport.ts) prüft nur `INBOUND_ENABLED` + eine
              vorhandene Mailbox, hat also keinen eigenen UID-Gate, den ein
              beta-freigeschalteter Tenant hier verletzen könnte — der Aufruf
              gelingt für den Beta-Account genauso wie für einen Nutzer, der
              über das allgemeine Flag Zugriff hat (Reviewer-Fix F-1,
              loop-a-inbound-beta). */}
          {canUseInbound(effectiveScopeUid) && !isReadOnly && currentUser && (
            <div className="mt-6">
              <InboundExportSection ownerUid={currentUser.uid} />
            </div>
          )}

          {/* IDV-6: Eingangsbelege-DATEV-Belegtransfer ("Ziel 2", §6). Bewusst
              OHNE `!isReadOnly` — architecture-inbound-datev.md §5.5 verlangt
              "Owner ODER aktiver StB" als Nutzerkreis (s. inboundDatevAccess.ts
              Kopfkommentar), anders als der WL-A8-Export/die Ausgabe-Buchung
              oben, die beide Owner-only bleiben. */}
          {canUseInboundDatev(effectiveScopeUid, resolveTier(sellerData), isReadOnly) && currentUser && (
            <div className="mt-6">
              <InboundDatevExportSection docs={docs} sellerDatevConfig={sellerDatevConfig} />
            </div>
          )}
        </div>
      </div>

      {/* Expense modal in list view */}
      {bookingDoc && (
        <ExpenseBookingModal
          open={true}
          invoice={inboundToParsedZugferd(bookingDoc)}
          onClose={() => setBookingDoc(null)}
          onBooked={handleExpenseBooked}
        />
      )}
    </>
  );
};

export default InboxPage;
